import React, { useEffect, useState } from 'react';
import {
    collection,
    doc,
    getDoc,
    setDoc,
} from 'firebase/firestore';
import { db } from '../firebase-config'; // adjust path if needed
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const DOC_PATH = { collection: 'cutoffNotifications', id: 'globalConfig' };

const initialState = {
    cutoffTime: '22:00',
    timezone: 'Europe/London',   // ✅ UK timezone
    startOffsetMinutes: 60,
    endOffsetMinutes: 0,
    frequencyMinutes: 15,        // ✅ default 15
    title: 'Order before cutoff',
    body: 'Please place your order before cutoff time.',
    imageUrl: '',
    isActive: false,
};

const CutoffNotificationsSettings = () => {
    const storage = getStorage();
    const [form, setForm] = useState(initialState);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [savedAt, setSavedAt] = useState(null);
    const [previewTimes, setPreviewTimes] = useState([]);
    const [uploading, setUploading] = useState(false);


    const handleImageUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setError('');

        try {
            const storageRef = ref(storage, `cutoffNotifications/${Date.now()}.jpg`); // ✅ Fixed filename
            await uploadBytes(storageRef, file);  // ✅ NO metadata
            const url = await getDownloadURL(storageRef);
            console.log('✅ Uploaded image URL:', url); // Debug
            setForm(prev => ({ ...prev, imageUrl: url }));
        } catch (e) {
            console.error('Image upload error:', e);
            setError('Failed to upload image. Please try again.');
        } finally {
            setUploading(false);
        }
    };





    const handleRemoveImage = async () => {
        if (!form.imageUrl) {
            setForm(prev => ({ ...prev, imageUrl: '' }));
            return;
        }

        setError('');
        try {
            // Optional: delete from storage by URL (only if you want to clean up)
            const imageRef = ref(storage, form.imageUrl);
            await deleteObject(imageRef).catch(() => {
                // ignore if not found or permission issue
            });
        } catch (e) {
            console.error('Error deleting image from storage', e);
        } finally {
            setForm(prev => ({ ...prev, imageUrl: '' }));
        }
    };


    const handleChange = (field, value) => {
        setForm(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    // ✅ Safe numeric handler: allows empty input, converts to number only on save
    const handleNumberChange = (field, value) => {
        // Allow empty string in the UI so user can clear and type
        if (value === '') {
            setForm(prev => ({ ...prev, [field]: '' }));
            return;
        }
        // Only digits
        if (!/^-?\d*$/.test(value)) return;
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const loadConfig = async () => {
        setLoading(true);
        setError('');
        try {
            const docRef = doc(collection(db, DOC_PATH.collection), DOC_PATH.id);
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                const data = snap.data();
                setForm(prev => ({
                    ...prev,
                    ...data,
                }));
            } else {
                setForm(initialState);
            }
        } catch (e) {
            console.error('Error loading cutoffNotifications config', e);
            setError('Failed to load configuration. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const computePreviewTimes = () => {
        try {
            let {
                cutoffTime,
                startOffsetMinutes,
                endOffsetMinutes,
                frequencyMinutes,
            } = form;

            const [h, m] = String(cutoffTime || '22:00').split(':').map(Number);
            if (isNaN(h) || isNaN(m)) {
                setPreviewTimes([]);
                return;
            }

            // Parse numeric fields; fall back to defaults if empty/invalid
            const start = parseInt(startOffsetMinutes, 10);
            const end = parseInt(endOffsetMinutes, 10);
            const freq = parseInt(frequencyMinutes, 10);

            if (isNaN(start) || isNaN(end) || isNaN(freq) || freq <= 0) {
                setPreviewTimes([]);
                return;
            }

            const now = new Date();
            const cutoff = new Date(now);
            cutoff.setHours(h, m, 0, 0);

            const list = [];
            for (let offset = start; offset >= end; offset -= freq) {
                const t = new Date(cutoff.getTime() - offset * 60000);
                list.push(t);
            }
            setPreviewTimes(list);
        } catch (e) {
            console.error('Error computing preview times', e);
            setPreviewTimes([]);
        }
    };

    useEffect(() => {
        loadConfig();
    }, []);

    useEffect(() => {
        computePreviewTimes();
    }, [form.cutoffTime, form.startOffsetMinutes, form.endOffsetMinutes, form.frequencyMinutes]);

    const handleSave = async e => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const docRef = doc(collection(db, DOC_PATH.collection), DOC_PATH.id);

            const payload = {
                cutoffTime: form.cutoffTime || '22:00',
                timezone: form.timezone || 'Europe/London',
                startOffsetMinutes: parseInt(form.startOffsetMinutes, 10) || 0,
                endOffsetMinutes: parseInt(form.endOffsetMinutes, 10) || 0,
                frequencyMinutes: parseInt(form.frequencyMinutes, 10) || 15,
                title: form.title || '',
                body: form.body || '',
                imageUrl: form.imageUrl || '',
                isActive: !!form.isActive,
            };

            await setDoc(docRef, payload, { merge: true });
            setSavedAt(new Date());
        } catch (e) {
            console.error('Error saving cutoffNotifications config', e);
            setError('Failed to save configuration. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={styles.page}>
                <div style={styles.card}>
                    <h2 style={styles.title}>Cutoff Notifications</h2>
                    <p>Loading configuration...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <h2 style={styles.title}>Cutoff Notifications</h2>
                <p style={styles.subtitle}>
                    Configure periodic alerts to remind users to place orders before the cutoff time.
                </p>

                {error && <div style={styles.error}>{error}</div>}

                <form onSubmit={handleSave}>
                    <div style={styles.section}>
                        <label style={styles.label}>Status</label>
                        <label style={styles.checkboxRow}>
                            <input
                                type="checkbox"
                                checked={!!form.isActive}
                                onChange={e => handleChange('isActive', e.target.checked)}
                            />
                            <span>Enable cutoff reminder notifications</span>
                        </label>
                    </div>

                    <div style={styles.row}>
                        <div style={styles.col}>
                            <label style={styles.label}>Cutoff Time (HH:mm)</label>
                            <input
                                type="time"
                                value={form.cutoffTime}
                                onChange={e => handleChange('cutoffTime', e.target.value)}
                                style={styles.input}
                            />
                        </div>
                        <div style={styles.col}>
                            <label style={styles.label}>Timezone</label>
                            <input
                                type="text"
                                value={form.timezone}
                                onChange={e => handleChange('timezone', e.target.value)}
                                placeholder="Europe/London"
                                style={styles.input}
                            />
                            <div style={styles.helper}>Default: Europe/London (UK)</div>
                        </div>
                    </div>

                    <div style={styles.row}>
                        <div style={styles.col}>
                            <label style={styles.label}>Start Offset (minutes before cutoff)</label>
                            <input
                                type="text"
                                value={form.startOffsetMinutes}
                                onChange={e => handleNumberChange('startOffsetMinutes', e.target.value)}
                                style={styles.input}
                            />
                            <div style={styles.helper}>Example: 60 = start 1 hour before cutoff.</div>
                        </div>
                        <div style={styles.col}>
                            <label style={styles.label}>End Offset (minutes before cutoff)</label>
                            <input
                                type="text"
                                value={form.endOffsetMinutes}
                                onChange={e => handleNumberChange('endOffsetMinutes', e.target.value)}
                                style={styles.input}
                            />
                            <div style={styles.helper}>Example: 0 = last reminder at cutoff time.</div>
                        </div>
                        <div style={styles.col}>
                            <label style={styles.label}>Frequency (minutes)</label>
                            <input
                                type="text"
                                value={form.frequencyMinutes}
                                onChange={e => handleNumberChange('frequencyMinutes', e.target.value)}
                                style={styles.input}
                            />
                            <div style={styles.helper}>Example: 15 = every 15 minutes.</div>
                        </div>
                    </div>

                    <div style={styles.section}>
                        <label style={styles.label}>Notification Title</label>
                        <input
                            type="text"
                            value={form.title}
                            onChange={e => handleChange('title', e.target.value)}
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.section}>
                        <label style={styles.label}>Notification Body</label>
                        <textarea
                            value={form.body}
                            onChange={e => handleChange('body', e.target.value)}
                            rows={3}
                            style={styles.textarea}
                        />
                    </div>

                    {/* <div style={styles.section}>
                        <label style={styles.label}>Notification Image</label>

                        {form.imageUrl ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <img
                                    src={form.imageUrl}
                                    alt="Notification"
                                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid #e5e7eb' }}
                                />
                                <button
                                    type="button"
                                    onClick={handleRemoveImage}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: 4,
                                        border: '1px solid #ef4444',
                                        background: '#fee2e2',
                                        color: '#b91c1c',
                                        fontSize: 13,
                                        cursor: 'pointer',
                                    }}
                                >
                                    Remove image
                                </button>
                            </div>
                        ) : (
                            <div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    disabled={uploading}
                                />
                                <div style={styles.helper}>
                                    {uploading ? 'Uploading...' : 'Upload an image to show with the notification.'}
                                </div>
                            </div>
                        )}
                    </div> */}


                    <div style={styles.footerRow}>
                        <button
                            type="submit"
                            disabled={saving}
                            style={styles.saveButton}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                        {savedAt && (
                            <span style={styles.savedAt}>
                                Saved at {savedAt.toLocaleTimeString()}
                            </span>
                        )}
                    </div>
                </form>
            </div>

            <div style={styles.card}>
                <h3 style={styles.title}>Preview reminder times (today)</h3>
                {previewTimes.length === 0 ? (
                    <p style={styles.helper}>No valid times based on current settings.</p>
                ) : (
                    <ul style={{ paddingLeft: 18, marginTop: 8 }}>
                        {previewTimes.map((t, idx) => (
                            <li key={idx}>
                                {t.toLocaleTimeString()} (browser local time)
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

const styles = {
    page: {
        padding: 24,
        background: '#f4f5f7',
        minHeight: '100vh',
    },
    card: {
        background: '#fff',
        borderRadius: 8,
        padding: 20,
        boxShadow: '0 1px 3px rgba(15,23,42,0.12)',
        marginBottom: 20,
    },
    title: {
        margin: 0,
        marginBottom: 8,
        fontSize: 20,
        fontWeight: 600,
        color: '#111827',
    },
    subtitle: {
        margin: 0,
        marginBottom: 16,
        fontSize: 14,
        color: '#6b7280',
    },
    section: {
        marginBottom: 16,
    },
    label: {
        display: 'block',
        fontWeight: 600,
        marginBottom: 4,
        fontSize: 13,
        color: '#374151',
    },
    input: {
        width: '100%',
        padding: '8px 10px',
        borderRadius: 4,
        border: '1px solid #d1d5db',
        fontSize: 14,
    },
    textarea: {
        width: '100%',
        padding: '8px 10px',
        borderRadius: 4,
        border: '1px solid #d1d5db',
        fontSize: 14,
        resize: 'vertical',
    },
    row: {
        display: 'flex',
        gap: 16,
        marginBottom: 16,
        alignItems: 'flex-start',
    },
    col: {
        flex: 1,
    },
    checkboxRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        color: '#374151',
    },
    helper: {
        marginTop: 4,
        fontSize: 12,
        color: '#6b7280',
    },
    footerRow: {
        marginTop: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    saveButton: {
        padding: '8px 16px',
        fontWeight: 600,
        fontSize: 14,
        borderRadius: 4,
        border: 'none',
        color: '#fff',
        background: '#2563eb',
        cursor: 'pointer',
    },
    savedAt: {
        fontSize: 12,
        color: '#6b7280',
    },
    error: {
        marginBottom: 12,
        padding: 8,
        borderRadius: 4,
        background: '#fee2e2',
        color: '#991b1b',
        fontSize: 13,
    },
};

export default CutoffNotificationsSettings;
